import { communicationInboxPath } from '@/lib/communicationInbox';

export const EMAIL_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const EMAIL_ATTACHMENT_MAX_COUNT = 5;

export const PATIENT_EMAIL_MESSAGE_STATUSES = [
  'queued',
  'sent',
  'delivered',
  'bounced',
  'complained',
  'failed',
  'received',
] as const;

export type PatientEmailMessageStatus = (typeof PATIENT_EMAIL_MESSAGE_STATUSES)[number];

const STATUS_LABELS: Record<PatientEmailMessageStatus, { key: string; fallback: string }> = {
  queued: { key: 'email.status_queued', fallback: 'I kø' },
  sent: { key: 'email.status_sent', fallback: 'Sendt' },
  delivered: { key: 'email.status_delivered', fallback: 'Leveret' },
  bounced: { key: 'email.status_bounced', fallback: 'Afvist' },
  complained: { key: 'email.status_complained', fallback: 'Klaget' },
  failed: { key: 'email.status_failed', fallback: 'Fejlet' },
  received: { key: 'email.status_received', fallback: 'Modtaget' },
};

const EMAIL_ATTACHMENT_MIME_ALLOW = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

export function isPatientEmailMessageStatus(value: string): value is PatientEmailMessageStatus {
  return (PATIENT_EMAIL_MESSAGE_STATUSES as readonly string[]).includes(value);
}

export function patientEmailStatusLabel(
  status: string,
  t: (key: string, fallback: string) => string,
): string {
  if (isPatientEmailMessageStatus(status)) {
    switch (status) {
      case 'queued':
      case 'sent':
      case 'delivered':
      case 'bounced':
      case 'complained':
      case 'failed':
      case 'received':
        return t(STATUS_LABELS[status].key, STATUS_LABELS[status].fallback);
      default: {
        const _exhaustive: never = status;
        return _exhaustive;
      }
    }
  }
  return status;
}

export function emailAttachmentAllowed(opts: {
  filename: string;
  contentType: string | null | undefined;
  sizeBytes: number;
}): boolean {
  if (opts.sizeBytes < 0 || opts.sizeBytes > EMAIL_ATTACHMENT_MAX_BYTES) return false;
  const name = opts.filename.toLowerCase();
  if (!name || name.includes('..') || name.includes('/')) return false;
  const mime = (opts.contentType ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
  if (mime && EMAIL_ATTACHMENT_MIME_ALLOW.has(mime)) return true;
  return /\.(pdf|jpe?g|png|gif|webp|txt|docx?|xlsx?)$/i.test(name);
}

export function validateEmailComposeAttachment(file: File): 'ok' | 'size' | 'type' {
  if (!emailAttachmentAllowed({
    filename: file.name,
    contentType: file.type,
    sizeBytes: file.size,
  })) {
    return file.size > EMAIL_ATTACHMENT_MAX_BYTES ? 'size' : 'type';
  }
  return 'ok';
}

export async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function formatEmailMailbox(
  address: string | null | undefined,
  displayName?: string | null,
): string {
  const email = (address ?? '').trim();
  const name = (displayName ?? '').trim();
  if (!email) return name;
  if (!name) return email;
  return `${name} <${email}>`;
}

export function formatPatientEmailPartyLine(opts: {
  from?: string | null;
  to?: string | null;
  fromLabel: string;
  toLabel: string;
}): string {
  const parts: string[] = [];
  if (opts.from) parts.push(`${opts.fromLabel}: ${opts.from}`);
  if (opts.to) parts.push(`${opts.toLabel}: ${opts.to}`);
  return parts.join(' · ');
}

export function staffNotificationHref(metadata: Record<string, unknown> | null | undefined): string {
  const patientId = typeof metadata?.patient_id === 'string' ? metadata.patient_id : null;
  const threadId = typeof metadata?.thread_id === 'string' ? metadata.thread_id : null;
  if (patientId) {
    const params = new URLSearchParams();
    params.set('tab', 'communication');
    params.set('channel', 'email');
    if (threadId) params.set('thread', threadId);
    return `/patients/${patientId}?${params.toString()}`;
  }
  return communicationInboxPath('email', { threadId });
}
